// @ts-nocheck
import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  Edit,
  Eye,
  Trash2,
  UserPlus,
  Users,
  X,
  SearchIcon,
  Building2Icon,
  UserCheckIcon,
  BriefcaseIcon,
  SlidersHorizontalIcon,
  RefreshCw,
  MoreVertical,
  AlertTriangle
} from "lucide-react";
import {
  useOrganizationMembers,
  useTeams,
  useResendOrganizationInvitation,
  useResendVerificationEmail,
} from "@/hooks/api-hooks";
import { useAuth } from "@/contexts/auth-context";
import { getAvatarColor, getInitials, cn } from "@/lib/utils";
import { toast } from "react-hot-toast";

// Mock usage hook for usage tracking
const useUsage = () => {
  return {
    usageData: {
      memberUsage: {
        current: 6,
        limit: 10,
        limitReached: false
      }
    },
    canPerformAction: (action: string) => true,
    showLimitReachedModal: (action: string) => console.log('Upgrade needed for:', action),
    refreshUsageData: async () => console.log('Refreshing usage data')
  };
};

// Real modal components
import { AddMemberModal } from "@/components/modals/AddMemberModal";
import { EditMemberModal } from "@/components/modals/EditMemberModal";
import { DeleteMemberModal } from "@/components/modals/DeleteMemberModal";
import { BulkDeleteMembersModal } from "@/components/modals/BulkDeleteMembersModal";
import { TeamMemberProfileModal } from "@/components/modals/TeamMemberProfileModal";
import { OneOnOneScheduleModal } from "@/components/modals/OneOnOneScheduleModal";

// Usage warning banner component
const UsageWarningBanner = ({ actionType, threshold, className }: any) => {
  const { usageData } = useUsage();

  if (!usageData || usageData.memberUsage.current / usageData.memberUsage.limit < threshold / 100) {
    return null;
  }

  return (
    <div className={cn("bg-yellow-50 border border-yellow-200 rounded-lg p-4", className)}>
      <div className="flex items-center gap-3">
        <AlertTriangle className="h-5 w-5 text-yellow-600" />
        <div className="flex-1">
          <h3 className="text-sm font-medium text-yellow-800">Approaching Member Limit</h3>
          <p className="text-sm text-yellow-700">
            You're using {usageData.memberUsage.current} of {usageData.memberUsage.limit} available members.
          </p>
        </div>
      </div>
    </div>
  );
};

export function MembersPage() {
  const navigate = useNavigate();
  const { usageData, canPerformAction, showLimitReachedModal, refreshUsageData } = useUsage();

  // Auth and hooks
  const { user: authData, isLoading: authLoading } = useAuth();
  const organizationId = authData?.currentOrganizationId || 'org-1';

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedRole, setSelectedRole] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [editMemberOpen, setEditMemberOpen] = useState(false);
  const [deleteMemberOpen, setDeleteMemberOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<any | null>(null);
  const [selectedMembers, setSelectedMembers] = useState<Set<string>>(new Set());
  const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);

  // Check if user has permission to access organization members
  const userRole = authData?.role || authData?.organizationRole;
  const canAccessMembers = userRole === 'owner' || userRole === 'admin';

  // API calls - only if user has access
  const { data: membersData, isLoading: membersLoading, error: membersError } = useOrganizationMembers(
    canAccessMembers ? organizationId : '',
    undefined,
    {
      enabled: canAccessMembers && !!organizationId,
      retry: (failureCount, error) => {
        if (error?.status === 401 || error?.status === 403) {
          return failureCount < 2;
        }
        return false;
      },
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    }
  );

  const { data: teamsData, isLoading: teamsLoading } = useTeams(
    canAccessMembers ? { includeMembers: true } : { includeMembers: false }
  );

  const resendInvitationMutation = useResendOrganizationInvitation();
  const resendVerificationMutation = useResendVerificationEmail();
  const isResendingWelcome = resendInvitationMutation.isPending || resendVerificationMutation.isPending;

  // Debug logging
  useEffect(() => {
    if (authData && !canAccessMembers) {
      console.log('🚫 Staff user accessed /members page - showing 403:', {
        userId: authData?.id,
        role: userRole,
        email: authData?.email,
        willMakeApiCall: false
      });
    } else if (authData && canAccessMembers) {
      console.log('✅ Admin/Owner user accessed /members page:', {
        userId: authData?.id,
        role: userRole,
        email: authData?.email,
        willMakeApiCall: true
      });
    }
  }, [authData, canAccessMembers, userRole]);

  useEffect(() => {
    if (membersError) {
      console.log('⚠️ Members API error (should not cause logout):', {
        error: membersError,
        status: membersError?.status,
        message: membersError?.message,
        canAccessMembers,
        userRole
      });
    }
  }, [membersError, canAccessMembers, userRole]);

  const members = useMemo(() => {
    return (membersData?.data || []).map((member: any) => {
      const user = member.user ?? {};
      const isInvitation = Boolean(member.isInvitation ?? member.is_invitation);
      const invitedFirstName = member.invitedFirstName ?? member.invited_first_name;
      const invitedLastName = member.invitedLastName ?? member.invited_last_name;
      const email = user.email ?? member.email ?? '';

      return {
        id: member.id ?? user.id ?? `member-${user.email ?? Math.random()}`,
        firstName: user.firstName ?? user.first_name ?? invitedFirstName ?? '',
        lastName: user.lastName ?? user.last_name ?? invitedLastName ?? '',
        email,
        phone: user.phone ?? '',
        avatar: user.avatar ?? '',
        jobTitle: user.jobTitle ?? user.job_title ?? '',
        department: user.department ?? '',
        organizationRole: (member.role || '').toLowerCase(),
        joinedAt: member.joinedAt ?? member.joined_at ?? new Date().toISOString(),
        isActive: Boolean(member.isActive ?? member.is_active ?? !isInvitation),
        userIsActive: Boolean(user.isActive ?? user.is_active ?? !isInvitation),
        status: isInvitation
          ? 'pending'
          : (user.isActive ?? user.is_active ?? true) ? 'active' : (member.isActive ?? member.is_active ? 'pending' : 'inactive'),
        lastLoginAt: user.lastLoginAt ?? user.last_login_at ?? undefined,
        isInvitation,
        invitationExpiresAt: member.invitationExpiresAt ?? member.invitation_expires_at ?? null,
        user: {
          id: user.id,
          firstName: user.firstName ?? user.first_name ?? '',
          lastName: user.lastName ?? user.last_name ?? '',
          email,
          department: user.department ?? '',
          jobTitle: user.jobTitle ?? user.job_title ?? '',
          avatar: user.avatar ?? '',
          phone: user.phone ?? '',
          isActive: user.isActive ?? user.is_active ?? !isInvitation,
        },
      };
    });
  }, [membersData]);

  const teams = useMemo(() => {
    if (!teamsData) return [];
    if (Array.isArray((teamsData as any).teams)) return (teamsData as any).teams;
    if (Array.isArray((teamsData as any).data)) return (teamsData as any).data;
    return [];
  }, [teamsData]);

  // Derived data for filters
  const departments = useMemo(() => {
    const depts = [...new Set(members.map((user: any) => user.department).filter(Boolean))];
    return depts.sort();
  }, [members]);

  const roles = useMemo(() => {
    const userRoles = [...new Set(members.map((user: any) => user.organizationRole).filter(Boolean))];
    return userRoles.sort();
  }, [members]);

  // Filtered and sorted users
  const filteredUsers = useMemo(() => {
    const filtered = members.filter((user: any) => {
      const matchesSearch = searchQuery === "" ||
        (user.firstName ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (user.lastName ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (user.email ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.jobTitle?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.department?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesDepartment = selectedDepartment === "all" || user.department === selectedDepartment;
      const matchesStatus = selectedStatus === "all" || user.status === selectedStatus;
      const matchesRole = selectedRole === "all" || user.organizationRole === selectedRole;

      return matchesSearch && matchesDepartment && matchesStatus && matchesRole;
    });

    // Sort users
    filtered.sort((a: any, b: any) => {
      switch (sortBy) {
        case "name":
          return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
        case "department":
          return (a.department || "").localeCompare(b.department || "");
        case "role":
          return (a.organizationRole || "").localeCompare(b.organizationRole || "");
        case "startDate":
          return new Date(b.joinedAt || b.createdAt).getTime() - new Date(a.joinedAt || a.createdAt).getTime();
        default:
          return 0;
      }
    });

    return filtered;
  }, [members, searchQuery, selectedDepartment, selectedStatus, selectedRole, sortBy]);

  // Clear selection when filters change
  React.useEffect(() => {
    setSelectedMembers(new Set());
  }, [searchQuery, selectedDepartment, selectedStatus, selectedRole]);

  // Handle unauthenticated users
  if (!authData) {
    if (authLoading || membersLoading || teamsLoading) {
      return <div>Loading...</div>;
    }

    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 rounded-full bg-muted flex items-center justify-center">
            <Users className="w-8 h-8 text-muted-foreground" />
          </div>
          <h2 className="text-2xl font-semibold">Session required</h2>
          <p className="text-muted-foreground max-w-sm">
            You need to be signed in to manage organization members.
          </p>
          <div className="flex items-center justify-center gap-3">
            <Button onClick={() => navigate('/login')}>Go to login</Button>
            <Button variant="outline" onClick={() => navigate('/')}>Back home</Button>
          </div>
        </div>
      </div>
    );
  }

  // Handle unauthorized access
  if (!canAccessMembers) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md mx-auto">
          <div className="mx-auto w-24 h-24 bg-orange-100 rounded-full flex items-center justify-center mb-6">
            <Users className="w-12 h-12 text-orange-600" />
          </div>
          <div className="text-6xl font-bold text-muted-foreground mb-4">403</div>
          <h2 className="text-2xl font-bold mb-4">Access Forbidden</h2>
          <p className="text-muted-foreground mb-6">
            You don't have permission to access organization members. This feature is only available to administrators and owners.
          </p>
          <div className="space-y-3">
            <Button onClick={() => navigate('/dashboard')} className="w-full">
              Return to Dashboard
            </Button>
            <Button onClick={() => navigate(-1)} variant="outline" className="w-full">
              Go Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Multi-select handlers
  const toggleMemberSelection = (memberId: string) => {
    const newSelected = new Set(selectedMembers);
    if (newSelected.has(memberId)) {
      newSelected.delete(memberId);
    } else {
      newSelected.add(memberId);
    }
    setSelectedMembers(newSelected);
  };

  const toggleAllMembers = () => {
    if (selectedMembers.size === filteredUsers.length) {
      setSelectedMembers(new Set());
    } else {
      setSelectedMembers(new Set(filteredUsers.map((user: any) => user.id)));
    }
  };

  const handleBulkDelete = () => {
    setBulkDeleteModalOpen(true);
  };

  const clearSelection = () => {
    setSelectedMembers(new Set());
  };

  const handleResendWelcomeLink = (member: any) => {
    if (!organizationId) {
      toast.error('Organization not found');
      return;
    }

    if (!member.email) {
      toast.error('No email found for this member.');
      return;
    }

    if (member.isInvitation) {
      resendInvitationMutation.mutate(
        { organizationId, invitationId: member.id },
        {
          onSuccess: () => {
            toast.success(`Invitation email resent to ${member.email}`);
          },
          onError: (error: any) => {
            console.error('Error resending invitation:', error);
            toast.error(error?.message || 'Failed to resend invitation email');
          },
        }
      );
      return;
    }

    resendVerificationMutation.mutate(
      { email: member.email },
      {
        onSuccess: () => {
          toast.success(`Verification email resent to ${member.email}`);
        },
        onError: (error: any) => {
          console.error('Error resending verification email:', error);
          toast.error(error?.message || 'Failed to resend verification email');
        },
      }
    );
  };

  const handleAddMember = () => {
    if (!canPerformAction('member')) {
      showLimitReachedModal('member');
      return;
    }
    setAddMemberOpen(true);
  };

  const handleMemberAdded = async (_memberData?: any) => {
    toast.success('Invitation sent successfully');
    await refreshUsageData();
  };

  const handleMemberUpdated = () => {
    toast.success('Member details updated successfully');
  };

  const handleMemberDeleted = async (_memberId?: string, _options?: any) => {
    toast.success('Member removed successfully');
    await refreshUsageData();
  };

  const handleBulkMemberDeleted = async (_memberIds?: string[], _options?: any) => {
    toast.success('Selected members removed successfully');
    setSelectedMembers(new Set());
    setBulkDeleteModalOpen(false);
    await refreshUsageData();
  };

  // Show loading state
  if (membersLoading || teamsLoading) {
    return (
      <div className="bg-background min-h-screen">
        <div className="flex items-center justify-center py-20">
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-border border-t-foreground"></div>
            <span className="text-muted-foreground">Loading members...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background min-h-screen">
      <div className="space-y-8 p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-card p-6 rounded-lg border border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-lg flex items-center justify-center">
                <Users className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground">Members</h1>
                <p className="text-muted-foreground">Manage all organization members</p>
                {/* Member Usage Display */}
                {usageData && (
                  <div className="flex items-center gap-3 mt-4">
                    <div className="text-sm text-muted-foreground">
                      <span className="font-medium">{usageData.memberUsage.current} of {usageData.memberUsage.limit}</span> members
                    </div>
                    {usageData.memberUsage.limitReached && (
                      <Badge variant="destructive" className="text-xs">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        Limits Reached
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button
                onClick={handleAddMember}
                className="bg-foreground hover:bg-foreground/90"
                disabled={!canPerformAction('member')}
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Add Member
              </Button>
              {!canPerformAction('member') && (
                <div className="text-sm text-destructive">
                  <button
                    type="button"
                    onClick={() => showLimitReachedModal('member')}
                    className="underline hover:no-underline"
                  >
                    Upgrade plan
                  </button> to add more members.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Usage Warning Banner */}
        <UsageWarningBanner
          actionType="member"
          threshold={80}
          className="mb-8"
        />

        {/* Search and Filters */}
        <div className="bg-card rounded-lg border border-border p-6 space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <SearchIcon className="h-5 w-5 text-muted-foreground" />
              <h2 className="text-lg font-semibold text-foreground">Find Members</h2>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="gap-2"
            >
              <SlidersHorizontalIcon className="h-4 w-4" />
              {showAdvancedFilters ? 'Simple' : 'Advanced'} Filters
            </Button>
          </div>

          <div className="space-y-4">
            {/* Primary Search */}
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-5 w-5" />
              <Input
                placeholder="Search by name, email, job title, or department..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12"
              />
            </div>

            {/* Filter Controls */}
            <div className="flex flex-wrap items-center gap-3">
              <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
                <SelectTrigger className="w-44">
                  <div className="flex items-center gap-2">
                    <Building2Icon className="h-4 w-4 text-muted-foreground" />
                    <SelectValue placeholder="All Departments" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  {departments.map(dept => (
                    <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger className="w-32">
                  <div className="flex items-center gap-2">
                    <UserCheckIcon className="h-4 w-4 text-muted-foreground" />
                    <SelectValue placeholder="Status" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>

              {showAdvancedFilters && (
                <>
                  <Select value={selectedRole} onValueChange={setSelectedRole}>
                    <SelectTrigger className="w-36">
                      <div className="flex items-center gap-2">
                        <BriefcaseIcon className="h-4 w-4 text-muted-foreground" />
                        <SelectValue placeholder="Role" />
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Roles</SelectItem>
                      {roles.map(role => (
                        <SelectItem key={role} value={role}>{role}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Sort by" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="name">Name (A-Z)</SelectItem>
                      <SelectItem value="department">Department</SelectItem>
                      <SelectItem value="role">Role</SelectItem>
                      <SelectItem value="startDate">Start Date</SelectItem>
                    </SelectContent>
                  </Select>
                </>
              )}
            </div>

            {/* Results Summary and Clear Filters */}
            {(searchQuery || selectedDepartment !== "all" || selectedStatus !== "all" || selectedRole !== "all") && (
              <div className="flex items-center justify-between pt-2 border-t border-border">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span className="font-medium">{filteredUsers.length}</span>
                  <span>of {members.length} members found</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSearchQuery("");
                    setSelectedDepartment("all");
                    setSelectedStatus("all");
                    setSelectedRole("all");
                  }}
                  className="text-muted-foreground hover:text-foreground"
                >
                  Clear all filters
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="bg-card rounded-lg border border-border">
          <div className="px-6 py-4 border-b border-border">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold text-foreground">Member Directory</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {filteredUsers.length} {filteredUsers.length === 1 ? 'member' : 'members'}
                  {(searchQuery || selectedDepartment !== "all" || selectedStatus !== "all" || selectedRole !== "all")
                    ? ` matching your search`
                    : ` in your organization`}
                </p>
              </div>
            </div>
          </div>

          <div className="p-6">
            {filteredUsers.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-20 h-20 bg-muted rounded-lg flex items-center justify-center mx-auto mb-6">
                  <Users className="h-10 w-10 text-muted-foreground" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-2">No members found</h3>
                <p className="text-muted-foreground max-w-md mx-auto mb-8">
                  We couldn't find any members matching your current filters.
                  Try adjusting your search criteria.
                </p>
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchQuery("");
                    setSelectedDepartment("all");
                    setSelectedStatus("all");
                    setSelectedRole("all");
                  }}
                >
                  Clear all filters
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Table Header */}
                <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-muted/50 rounded-lg text-sm font-medium text-muted-foreground">
                  <div className="col-span-1 flex items-center">
                    <Checkbox
                      checked={selectedMembers.size === filteredUsers.length && filteredUsers.length > 0}
                      onCheckedChange={toggleAllMembers}
                    />
                  </div>
                  <div className="col-span-4">Name</div>
                  <div className="col-span-2">Department</div>
                  <div className="col-span-2">Role</div>
                  <div className="col-span-2">Status</div>
                  <div className="col-span-1">Actions</div>
                </div>

                {/* Table Body */}
                {filteredUsers.map((member: any) => (
                  <div
                    key={member.id}
                    className="grid grid-cols-12 gap-4 px-4 py-3 border border-border rounded-lg hover:border-border/80 transition-colors"
                  >
                    <div className="col-span-1 flex items-center">
                      <Checkbox
                        checked={selectedMembers.has(member.id)}
                        onCheckedChange={() => toggleMemberSelection(member.id)}
                      />
                    </div>

                    <div className="col-span-4 flex items-center gap-3">
                      <Avatar className="h-8 w-8 border">
                        <AvatarImage src={member.avatar || undefined} alt={`${member.firstName} ${member.lastName}`} />
                        <AvatarFallback className={`font-semibold text-sm ${getAvatarColor(member.firstName, member.lastName)}`}>
                          {getInitials(member.firstName, member.lastName)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="font-medium">{member.firstName} {member.lastName}</div>
                        <div className="text-xs text-muted-foreground">{member.email}</div>
                      </div>
                    </div>

                    <div className="col-span-2 flex items-center">
                      <Badge variant="outline">{member.department || 'No Department'}</Badge>
                    </div>

                    <div className="col-span-2 flex items-center">
                      <Badge variant="secondary">{member.organizationRole}</Badge>
                    </div>

                    <div className="col-span-2 flex items-center">
                      <Badge
                        variant={
                          member.status === "active"
                            ? "default"
                            : member.status === "pending"
                            ? "outline"
                            : "secondary"
                        }
                      >
                        {member.status}
                      </Badge>
                    </div>

                    <div className="col-span-1 flex items-center">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedMember(member);
                              setProfileModalOpen(true);
                            }}
                            className="cursor-pointer"
                          >
                            <Eye className="mr-2 h-4 w-4" />
                            View
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedMember(member);
                              setEditMemberOpen(true);
                            }}
                            className="cursor-pointer"
                          >
                            <Edit className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          {!member.userIsActive && (
                            <DropdownMenuItem
                              onClick={() => handleResendWelcomeLink(member)}
                              disabled={isResendingWelcome}
                              className="cursor-pointer"
                            >
                              <RefreshCw className="mr-2 h-4 w-4" />
                              Resend Welcome Link
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedMember(member);
                              setDeleteMemberOpen(true);
                            }}
                            className="cursor-pointer text-destructive focus:text-destructive"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Floating Bulk Actions Bar */}
        {selectedMembers.size > 0 && (
          <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
            <div className="bg-card border border-border rounded-lg shadow-lg p-4 flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-muted-foreground" />
                <span className="font-medium text-foreground">
                  {selectedMembers.size} member{selectedMembers.size !== 1 ? 's' : ''} selected
                </span>
              </div>
              <div className="h-6 w-px bg-border"></div>
              <div className="flex items-center gap-2">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleBulkDelete}
                  className="shadow-sm"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Selected
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearSelection}
                  className="shadow-sm"
                >
                  <X className="h-4 w-4 mr-2" />
                  Clear Selection
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <AddMemberModal
        isOpen={addMemberOpen}
        onClose={() => setAddMemberOpen(false)}
        onSuccess={handleMemberAdded}
        organizationId={organizationId}
        managers={members.filter((m: any) => !m.isInvitation && ['owner', 'admin', 'manager'].includes(m.organizationRole))}
        teams={teams}
      />

      <EditMemberModal
        memberId={selectedMember?.id}
        member={selectedMember}
        isOpen={editMemberOpen}
        onClose={() => {
          setEditMemberOpen(false);
          setSelectedMember(null);
        }}
        onSuccess={handleMemberUpdated}
        organizationId={organizationId}
        managers={members.filter((m: any) => !m.isInvitation && ['owner', 'admin', 'manager'].includes(m.organizationRole))}
        teams={teams}
      />

      <DeleteMemberModal
        member={selectedMember}
        isOpen={deleteMemberOpen}
        onClose={() => {
          setDeleteMemberOpen(false);
          setSelectedMember(null);
        }}
        onConfirm={handleMemberDeleted}
        organizationId={organizationId}
        replacementMembers={members.filter((u: any) => u.id !== selectedMember?.id)}
      />

      <BulkDeleteMembersModal
        selectedMembers={Array.from(selectedMembers).map(id => members.find(m => m.id === id)).filter(Boolean)}
        isOpen={bulkDeleteModalOpen}
        onClose={() => setBulkDeleteModalOpen(false)}
        onConfirm={handleBulkMemberDeleted}
        organizationId={organizationId}
        replacementMembers={members.filter((u: any) => !selectedMembers.has(u.id))}
      />

      <TeamMemberProfileModal
        member={selectedMember}
        isOpen={profileModalOpen}
        onClose={() => {
          setProfileModalOpen(false);
          setSelectedMember(null);
        }}
        onEdit={(memberId) => {
          setProfileModalOpen(false);
          setEditMemberOpen(true);
        }}
        onSendMessage={(memberId) => {
          console.log('Message functionality not available');
        }}
        onScheduleOneOnOne={(memberId) => {
          const member = members.find((u: any) => u.id === memberId);
          if (member) {
            setSelectedMember(member);
            setProfileModalOpen(false);
            setScheduleModalOpen(true);
          }
        }}
      />

      <OneOnOneScheduleModal
        isOpen={scheduleModalOpen}
        onClose={() => {
          setScheduleModalOpen(false);
          setSelectedMember(null);
        }}
        selectedMember={selectedMember}
        onSchedule={(scheduleData) => {
          const memberName = `${selectedMember?.firstName ?? ''} ${selectedMember?.lastName ?? ''}`.trim() || selectedMember?.email;
          toast.success(`1-on-1 with ${memberName} ${scheduleData.isRescheduled ? 'rescheduled' : 'scheduled'}.`);
        }}
      />
    </div>
  );
}
